import {
  SURECO_CSV_DATA_TYPE_OPTIONS,
  type SurecoCsvDataType,
} from '../constants'
import type { CsvCompareRow } from '../api/compare-data-api'

export function isSchemaMismatchError(msg: string): boolean {
  return /headers do not match .+ schema/i.test(msg)
}

export function buildFriendlySchemaError(
  rawMessage: string,
  selectedDataType: SurecoCsvDataType | undefined
): string {
  const typeLabel =
    SURECO_CSV_DATA_TYPE_OPTIONS.find((o) => o.value === selectedDataType)
      ?.label ??
    selectedDataType ??
    'selected type'

  return `This file doesn't match the "${typeLabel}" format. Please try again with the correct file or select a different data type.`
}

// Short letter+number codes (e.g. "ra01" for Rating Area 01) should keep
// their letters fully uppercase, e.g. "RA01" instead of "Ra01".
export function formatHeaderLabel(key: string): string {
  const codeMatch = key.match(/^([a-zA-Z]{1,4})(\d+)$/)
  if (codeMatch) return `${codeMatch[1].toUpperCase()}${codeMatch[2]}`

  return key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

export function isDiffField(row: CsvCompareRow, key: string): boolean {
  if (row.compareStatus !== 'MODIFIED') return false

  // Server-computed comparison — the same comparator the apply path uses to
  // decide what actually gets written, so this and apply are guaranteed to
  // agree on what "changed" means.
  return row.fieldCompare[key]?.isModified ?? false
}

/**
 * Resolve per-field validation errors for an ERROR row.
 *
 * EPS response shape: `row.errors.fieldErrors`.
 */
export function getFieldError(row: CsvCompareRow, key: string): string | null {
  if (row.compareStatus !== 'ERROR') return null

  const fieldErrors = row.errors?.fieldErrors

  if (fieldErrors?.[key]) return fieldErrors[key]

  // When the backend returned a fieldErrors map, only the listed fields are
  // errors — do not paint every empty cell with a generic message.
  if (fieldErrors && Object.keys(fieldErrors).length > 0) return null

  // No per-field detail (empty map or missing) — use the row-level message
  // so the error row still has a hoverable cue on every cell.
  const generic = row.errors?.error
  if (typeof generic === 'string' && generic) return generic
  return 'This row failed validation'
}
